import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';
import Avatar from 'src/components/Avatar';
import Button from 'src/components/Button';
import FontAwesomeIcon from 'src/components/Icon';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import './ProfileImageField.scss';

interface ProfileImageFieldProps {
  imageUrl?: string;
  stagedPreviewUrl?: string | null;
  isStaged: boolean;
  name?: string;
  subtitle?: string;
  onSelectImage: (files: File[]) => void;
  onClearStagedImage: () => void;
  loading?: boolean;
  disabled?: boolean;
  error?: string;
  saveActionLabel?: string;
  avatarSize?: string;
}

const ProfileImageField = ({
  imageUrl,
  stagedPreviewUrl,
  isStaged,
  name,
  subtitle,
  onSelectImage,
  onClearStagedImage,
  loading = false,
  disabled = false,
  error,
  saveActionLabel = 'Save Changes',
  avatarSize = 'profile',
}: ProfileImageFieldProps) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);

  const displayUrl: string | undefined = stagedPreviewUrl ?? imageUrl;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: 'image/*',
    multiple: false,
    disabled,
    noKeyboard: true,
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onSelectImage(acceptedFiles);
      }
    },
  });

  const dropClass: string = classNames('profile-avatar-drop', {
    'is-dragging': isDragActive,
    'is-staged': isStaged,
  });

  return (
    <div className="profile-image-field">
      <div className={dropClass} {...getRootProps()}>
        <input {...getInputProps()} />
        <Avatar size={avatarSize} url={displayUrl} loading={loading} />
        <div className="drop-overlay">
          <FontAwesomeIcon glyph="cloud-upload-alt" weight="light" />
        </div>
      </div>

      <div className="profile-meta">
        <div className="profile-identity">
          {name && <span className="profile-name">{name}</span>}
          {name && subtitle && <span className="profile-sep">—</span>}
          {subtitle && <span className="profile-subtitle">{subtitle}</span>}
        </div>

        <p className="profile-hint">
          Click the photo or drag an image onto it to change it.
        </p>

        <div className="profile-actions">
          <Button
            type="button"
            size="small"
            color="teal"
            onClick={open}
            icon={<FontAwesomeIcon glyph="image" weight="light" />}
            label="Change picture"
          />
          <Button
            type="button"
            size="small"
            outline="teal"
            disabled={!displayUrl}
            onClick={() => setIsLightboxOpen(true)}
            icon={<FontAwesomeIcon glyph="eye" weight="light" />}
            label="View"
          />
          {isStaged && (
            <Button
              type="button"
              size="small"
              outline="gray"
              onClick={onClearStagedImage}
              icon={<FontAwesomeIcon glyph="arrow-left" weight="light" />}
              label="Undo"
            />
          )}
        </div>

        <p className={isStaged ? 'profile-status staged' : 'profile-status'}>
          {isStaged
            ? `New photo staged — click the "${saveActionLabel}" button below to apply it.`
            : 'No new photo staged.'}
        </p>

        {error && <p className="profile-error">{error}</p>}
      </div>

      <ReusableLightbox
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        image={displayUrl}
      />
    </div>
  );
};

export default ProfileImageField;
